import React, { useState, useEffect } from 'react';
import {
  Autocomplete,
  TextField,
  ListItem,
  InputAdornment,
  ListItemIcon,
  ListItemButton,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/SearchRounded';
import {
  PlainObject,
  User,
  SearchItem,
  isUserSearchItem,
  isAccountSearchItem,
  isProjectSearchItem,
  AccountDropdownDto,
  ProjectSearchDto,
} from '../../types';
import { is, cn } from '../../utils';
import { MemberInfoCell } from '../tableCellRenders';
import { EntityInfoCell } from '../tableCellRenders/EntityInfoCell';
import Fuse from 'fuse.js';
import { useNavigate, useLocation } from 'react-router-dom';
import { SearchListBoxComponent } from './components/SearchListboxComponent';
import { useSearchItems } from './UsersSearchBox.utils';
import { paths } from '../../constants/paths';

interface Props {
  options: User[];
  handleOpenClick: () => void;
  open: boolean;
  accounts: AccountDropdownDto[];
  projects: ProjectSearchDto[];
}

export function UsersSearchBox({
  options,
  handleOpenClick,
  open,
  accounts,
  projects,
}: Props): JSX.Element {
  const { pathname } = useLocation();
  const nav = useNavigate();
  const [searchValue, setSearchValue] = useState<SearchItem | null | string>(
    null,
  );

  const searchItems = useSearchItems(options, accounts, projects);

  const valueIsItem =
    !is.string(searchValue) &&
    !is.nullOrUndefined(searchValue) &&
    is.object(searchValue);

  const getNavPath = (item: SearchItem): string => {
    if (isAccountSearchItem(item)) {
      return `${paths.portfolio}/${item.id}/${paths.ratesAndMargins}`;
    }
    if (isProjectSearchItem(item)) {
      return `${paths.portfolio}/${item.accountId}/${item.id}/${paths.ratesAndMargins}`;
    }
    return `${paths.contractors}/${item.departmentId}/${item.id}`;
  };

  useEffect(() => {
    if (!valueIsItem) {
      return;
    }

    nav(getNavPath(searchValue as SearchItem));
  }, [searchValue, valueIsItem, nav, open]);

  useEffect(() => {
    setSearchValue('');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pathname, valueIsItem]);

  const fuse = new Fuse(searchItems, {
    threshold: 0.35,
    keys: ['names', 'fullName', 'name'],
  });

  const getOptionLabel = (opt: string | SearchItem): string => {
    if (is.string(opt)) return opt;
    if (isUserSearchItem(opt)) return opt.fullName;
    return opt.name;
  };

  return (
    <>
      {open ? (
        <Autocomplete<SearchItem, false, false, true>
          freeSolo
          loading={options === null}
          size="small"
          value={searchValue}
          blurOnSelect
          onChange={(_, value) => setSearchValue(value)}
          options={searchItems}
          getOptionLabel={getOptionLabel}
          ListboxComponent={SearchListBoxComponent}
          className={cn(
            'MuiAutocomplete-searchBox',
            valueIsItem ? 'MuiAutocomplete-hasValue' : '',
          )}
          componentsProps={{
            popper: {
              id: 'search-popper',
              style: {
                width: '300px',
                boxShadow: '5px 5px 10px rgba(0, 0, 0, 0.1)',
              },
            },
          }}
          filterOptions={(items, state) => {
            return fuse.search(state.inputValue).map(({ item }) => item);
          }}
          renderOption={(props, item) => {
            if (isUserSearchItem(item)) {
              return (
                <ListItem {...props} key={item.id}>
                  <MemberInfoCell
                    hideAvatar={
                      // eslint-disable-next-line
                      (props as PlainObject)['data-option-index'] > 2000
                    }
                    userInfo={{
                      fullName: item.fullName,
                      avatarUrl: item.avatarUrl,
                      stream: item.designation,
                      country: item.country,
                      city: item.city,
                    }}
                    disabled={!!item.deactivatedAt}
                  />
                </ListItem>
              );
            }

            if (isAccountSearchItem(item)) {
              return (
                <ListItem {...props} key={item.id}>
                  <EntityInfoCell name={item.name} subLabel="Account" />
                </ListItem>
              );
            }

            return (
              <ListItem {...props} key={item.id}>
                <EntityInfoCell name={item.name} subLabel="Project" />
              </ListItem>
            );
          }}
          renderInput={(params) => (
            <TextField
              {...params}
              placeholder="Search"
              InputProps={{
                ...(params.InputProps || {}),
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon color="disabled" />
                  </InputAdornment>
                ),
              }}
            />
          )}
        />
      ) : (
        <ListItemButton
          onClick={handleOpenClick}
          sx={{
            pl: 0,
            pt: 1,
            pb: 0,
            borderRadius: 1,
            textAlign: 'center',
            pr: 0,
            py: 0,
            justifySelf: open ? 'flex-start' : 'center',
          }}
        >
          <ListItemIcon sx={{ justifyContent: 'center' }}>
            <SearchIcon color="disabled" />
          </ListItemIcon>
        </ListItemButton>
      )}
    </>
  );
}
